import type { MediaAsset } from '@moussenaires/shared';
import { formatBytes } from '@moussenaires/shared';
import { useMutation } from '@tanstack/react-query';
import { ImagePlus, Loader2, X } from 'lucide-react';
import { useCallback, useRef, useState, type ClipboardEvent, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { feedApi } from '../api/feed.js';
import { ApiClientError, mediaSrc } from '../api/client.js';
import './UploadDropzone.css';

export interface PendingAttachment {
  key: string;
  media: MediaAsset;
}

interface UploadDropzoneProps {
  attachments: PendingAttachment[];
  onAdd: (attachment: PendingAttachment) => void;
  onRemove: (key: string) => void;
  disabled?: boolean;
}

export function UploadDropzone({ attachments, onAdd, onRemove, disabled }: UploadDropzoneProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const uploadMutation = useMutation({
    mutationFn: (file: File) => feedApi.upload(file),
    onSuccess: (media) => {
      onAdd({ key: `${media.id}`, media });
      setError(null);
    },
    onError: (uploadError: unknown) => {
      const code = uploadError instanceof ApiClientError ? uploadError.code : 'internal_error';
      setError(t(`errors.${code}`, { defaultValue: t('media.uploadFailed') }));
    },
  });

  const handleFiles = useCallback(
    (files: FileList | File[]) => {
      for (const file of Array.from(files)) uploadMutation.mutate(file);
    },
    [uploadMutation],
  );

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setIsDragActive(false);
    if (event.dataTransfer.files.length > 0) handleFiles(event.dataTransfer.files);
  }

  function handlePaste(event: ClipboardEvent<HTMLDivElement>) {
    const files = Array.from(event.clipboardData.items)
      .filter((item) => item.kind === 'file')
      .map((item) => item.getAsFile())
      .filter((file): file is File => file !== null);
    if (files.length > 0) handleFiles(files);
  }

  return (
    <div className="upload-dropzone-wrap">
      <div
        className={`upload-dropzone ${isDragActive ? 'upload-dropzone-active' : ''}`}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragActive(true);
        }}
        onDragLeave={() => setIsDragActive(false)}
        onDrop={handleDrop}
        onPaste={handlePaste}
        onClick={() => !disabled && inputRef.current?.click()}
        role="button"
        tabIndex={0}
        aria-disabled={disabled}
      >
        {uploadMutation.isPending ? (
          <Loader2 className="upload-dropzone-spin" size={22} />
        ) : (
          <ImagePlus size={22} />
        )}
        <span>{isDragActive ? t('feed.dropzoneActive') : t('feed.dropzone')}</span>
        <input
          ref={inputRef}
          type="file"
          hidden
          multiple
          accept="image/*,video/*,audio/*"
          disabled={disabled}
          onChange={(event) => {
            if (event.target.files) handleFiles(event.target.files);
            event.target.value = '';
          }}
        />
      </div>

      {error && <p className="field-error">{error}</p>}

      {attachments.length > 0 && (
        <div className="upload-dropzone-previews">
          {attachments.map((attachment) => (
            <div key={attachment.key} className="upload-preview">
              {attachment.media.kind === 'image' ? (
                <img src={mediaSrc(attachment.media.url)} alt={attachment.media.originalName} />
              ) : (
                <div className="upload-preview-file">
                  <span>{attachment.media.originalName}</span>
                  <small>{formatBytes(attachment.media.sizeBytes)}</small>
                </div>
              )}
              <button
                type="button"
                className="upload-preview-remove"
                onClick={() => onRemove(attachment.key)}
                aria-label={t('common.delete')}
              >
                <X size={13} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
